/**
 * Companies Page — list and manage company/organization records.
 */

import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, Search, Plus, Users, Globe, Trash2 } from 'lucide-react'
import { useCRM } from '@/hooks/useCRM'
import { formatCents } from '@/utils/currency'
import { CompanyCreateModal } from '@/components/crm/CompanyCreateModal'
import { SortableHeader } from '@/components/ui/SortableHeader'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  EmptyState,
  Spinner,
  Input,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  useToast,
} from '@/components/ui'
import type { Company } from '@/types/crm'

export function CompaniesPage() {
  const { fetchCompanies, deleteCompany } = useCRM()
  const navigate = useNavigate()
  const { addToast } = useToast()

  const [companies, setCompanies] = useState<Company[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [showCreate, setShowCreate] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const load = useCallback(async () => {
    setIsLoading(true)
    const result = await fetchCompanies(search || undefined, 1, sortBy, sortOrder)
    setCompanies(result.companies)
    setIsLoading(false)
  }, [fetchCompanies, search, sortBy, sortOrder])

  useEffect(() => {
    const timer = setTimeout(load, 300)
    return () => clearTimeout(timer)
  }, [load])

  const handleSort = useCallback((newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy)
    setSortOrder(newSortOrder)
  }, [])

  const handleConfirmedDelete = useCallback(async () => {
    if (!confirmDelete) return
    setIsDeleting(true)
    const ok = await deleteCompany(confirmDelete)
    setIsDeleting(false)
    if (ok) {
      addToast({ type: 'success', title: 'Company deleted' })
      load()
    } else {
      addToast({ type: 'error', title: 'Delete failed' })
    }
    setConfirmDelete(null)
  }, [confirmDelete, deleteCompany, load, addToast])

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-border">
        <div className="py-3 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text" placeholder="Search companies..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex-1" />
          <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowCreate(true)}>
            Add Company
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading && companies.length === 0 ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : companies.length === 0 ? (
          <EmptyState icon={Building2} title="No companies yet" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHeader label="Company" sortKey="name" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={handleSort} className="px-6" />
                <SortableHeader label="Industry" sortKey="industry" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader label="Contacts" sortKey="contacts" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={handleSort} align="right" />
                <SortableHeader label="Revenue" sortKey="revenue" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={handleSort} align="right" />
                <TableHead>Domain</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.map((company) => (
                <TableRow key={company.id} onClick={() => navigate(`/crm/companies/${company.id}`)}
                  className="hover:bg-muted cursor-pointer group">
                  <TableCell className="px-6 py-3">
                    <div className="font-medium text-foreground">{company.name}</div>
                    {company.city && <div className="text-xs text-muted-foreground">{company.city}{company.country ? `, ${company.country}` : ''}</div>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{company.industry || '—'}</TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    <span className="flex items-center justify-end gap-1">
                      <Users className="w-3 h-3" />{company.totalContacts}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-medium text-foreground">
                    {formatCents(company.totalRevenueCents, company.currency)}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {company.domain && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{company.domain}</span>}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Delete company"
                      className="opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/10"
                      onClick={(e) => { e.stopPropagation(); setConfirmDelete(company.id) }}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {showCreate && <CompanyCreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load() }} />}

      {confirmDelete && (
        <Modal open onOpenChange={(o) => { if (!o) setConfirmDelete(null) }}>
          <ModalContent size="sm" showClose={false}>
            <ModalHeader><ModalTitle>Delete this company?</ModalTitle></ModalHeader>
            <ModalBody><p className="text-sm text-muted-foreground">This cannot be undone.</p></ModalBody>
            <ModalFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button variant="destructive" isLoading={isDeleting} onClick={handleConfirmedDelete}>Delete</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </div>
  )
}
